import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import BaseBreadcrumbs from '../../components/BaseBreadcrumbs'
import usePaginatedList from '../../hooks/usePaginatedList'
import PaginatedTable from '../../components/PaginatedTable'

const cx = (...c) => c.filter(Boolean).join(' ')

const Button = ({ as: Tag = 'button', className = '', children, ...rest }) => (
    <Tag
        className={cx(
            'inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium',
            'border bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700/80',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className
        )}
        {...rest}
    >
        {children}
    </Tag>
)

const DangerButton = (props) => (
    <Button
        {...props}
        className={cx(
            'border-red-800 bg-red-900/35 text-red-100 hover:bg-red-900/55',
            props.className
        )}
    />
)

const PrimaryButton = (props) => (
    <Button
        {...props}
        className={cx(
            'border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-500',
            props.className
        )}
    />
)

const Field = ({ label, children, help }) => (
    <label className="grid gap-1">
        <span className="text-xs text-slate-400 uppercase tracking-wide">
            {label}
        </span>
        {children}
        {help ? (
            <span className="text-[11px] text-slate-500">{help}</span>
        ) : null}
    </label>
)

const Input = (props) => (
    <input
        {...props}
        className={cx(
            'h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200',
            'placeholder:text-slate-500',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500/60',
            props.className
        )}
    />
)

const Select = ({ className = '', children, ...props }) => (
    <select
        {...props}
        className={cx(
            'h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500/60',
            className
        )}
    >
        {children}
    </select>
)

const Modal = ({ open, title, onClose, children, footer }) => {
    if (!open) return null
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-[min(880px,94vw)] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
                <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-800">
                    <h3 className="text-lg font-semibold text-slate-100">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className="p-4 md:p-6 space-y-4">{children}</div>
                {footer ? (
                    <div className="px-4 md:px-6 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

// ------------------------------------------------------
// Helpers
// ------------------------------------------------------
const prettyJSON = (value) => {
    try {
        return JSON.stringify(value, null, 2)
    } catch (e) {
        return String(value ?? '')
    }
}

const optionLabel = (o) => {
    if (!o) return ''
    const code = o.code ?? ''
    const name = o.name ?? ''
    return [code, name].filter(Boolean).join(' — ')
}

// ------------------------------------------------------
// Page
// ------------------------------------------------------
export default function PlanManager() {
    const clusterId = JSON.parse(localStorage.getItem('selected_cluster')).id

    // Endpoints
    const API_LIST_BASE = `/admin/plans/cluster/${clusterId}` // GET list / POST create
    const API_ADMIN_BASE = `/admin/plans` // GET/PUT/DELETE by id
    const API_PLAN_CATEGORIES = `/admin/plan-categories/`
    const API_COMPUTE_TYPES = `/admin/compute-types/cluster/${clusterId}/`

    // Dropdown options
    const [planCategories, setPlanCategories] = useState([])
    const [computeTypes, setComputeTypes] = useState([])
    const [loadingOptions, setLoadingOptions] = useState(false)

    // Filters (staged -> applied)
    const [draft, setDraft] = useState({
        search: '',
        plan_category_id: '',
        compute_type_id: '',
        is_active: '',
    })
    const [filters, setFilters] = useState(draft)

    // Fetcher for usePaginatedList
    const fetcher = useCallback(
        async (params) => {
            const { data } = await axiosInstance.get(API_LIST_BASE, { params })
            return data // {items, meta} or Array
        },
        [API_LIST_BASE]
    )

    const {
        rows,
        meta,
        loading,
        error,
        page,
        pageSize,
        onChange,
        getRequestParams,
        refetch,
        reloadCurrentPageSafe,
    } = usePaginatedList(fetcher, {
        initialPage: 1,
        initialPageSize: 25,
        deps: [filters],
    })

    // Load dropdown options
    const fetchOptions = useCallback(async () => {
        setLoadingOptions(true)
        try {
            const [catRes, ctRes] = await Promise.all([
                axiosInstance.get(API_PLAN_CATEGORIES, {
                    params: { page: 1, page_size: 1000 },
                }),
                axiosInstance.get(API_COMPUTE_TYPES, {
                    params: { page: 1, page_size: 1000 },
                }),
            ])

            const cats = Array.isArray(catRes?.data)
                ? catRes.data
                : Array.isArray(catRes?.data?.items)
                ? catRes.data.items
                : []
            const cts = Array.isArray(ctRes?.data)
                ? ctRes.data
                : Array.isArray(ctRes?.data?.items)
                ? ctRes.data.items
                : []

            setPlanCategories(cats)
            setComputeTypes(cts)
        } finally {
            setLoadingOptions(false)
        }
    }, [API_PLAN_CATEGORIES, API_COMPUTE_TYPES])

    useEffect(() => {
        // when cluster changes, reload options and reset page (handled by hook deps via filters change)
        fetchOptions()
    }, [clusterId, fetchOptions])

    // Columns (base + a few extra dynamic fields)
    const columns = useMemo(() => {
        const sample = rows?.[0] || {}
        const baseKeys = [
            'id',
            'code',
            'name',
            'vcpu',
            'ram_gb',
            'storage_gb',
            'price_per_month',
            'is_active',
            'compute_type_name',
            'plan_category_name',
        ]

        const extra = Object.keys(sample)
            .filter(
                (k) =>
                    ![
                        ...baseKeys,
                        'compute_type_id',
                        'plan_category_id',
                        'plan_category',
                        'cluster',
                        'cluster_id',
                        'created_at',
                        'updated_at',
                    ].includes(k)
            )
            .slice(0, 4)

        const all = [...baseKeys, ...extra]
        return all.map((key) => ({
            key,
            header: key.replaceAll('_', ' '),
            className:
                key === 'name' ? 'font-medium text-slate-100' : undefined,
            render: key === 'is_active' ? (v) => (v ? 'Yes' : 'No') : undefined,
        }))
    }, [rows])

    // Forms
    const [creating, setCreating] = useState(false)
    const [createForm, setCreateForm] = useState({
        code: '',
        name: '',
        openstack_id: '',
        plan_category_id: '',
        compute_type_id: '',
        vcpu: '',
        ram_gb: '',
        storage_gb: '',
        storage_kind: '',
        bandwidth_tb: '',
        price_per_month: '',
        is_active: true,
    })

    const [editing, setEditing] = useState(null)
    const [editForm, setEditForm] = useState({
        code: '',
        name: '',
        openstack_id: '',
        plan_category_id: '',
        compute_type_id: '',
        vcpu: '',
        ram_gb: '',
        storage_gb: '',
        storage_kind: '',
        bandwidth_tb: '',
        price_per_month: '',
        is_active: true,
    })

    const [confirmDel, setConfirmDel] = useState(null)

    // CRUD handlers
    const onCreate = async () => {
        // Basic validations (compute_type_id often NOT NULL in DB)
        if (!createForm.code?.trim() || !createForm.name?.trim()) {
            alert('Code and Name are required.')
            return
        }
        if (!createForm.compute_type_id) {
            alert('Compute Type is required.')
            return
        }

        const payload = {
            ...createForm,
            cluster_id: Number(clusterId),
            plan_category_id: createForm.plan_category_id
                ? Number(createForm.plan_category_id)
                : undefined,
            compute_type_id: createForm.compute_type_id
                ? Number(createForm.compute_type_id)
                : undefined,
            vcpu: createForm.vcpu !== '' ? Number(createForm.vcpu) : undefined,
            ram_gb:
                createForm.ram_gb !== ''
                    ? Number(createForm.ram_gb)
                    : undefined,
            storage_gb:
                createForm.storage_gb !== ''
                    ? Number(createForm.storage_gb)
                    : undefined,
            bandwidth_tb:
                createForm.bandwidth_tb === ''
                    ? undefined
                    : String(createForm.bandwidth_tb),
            price_per_month:
                createForm.price_per_month === ''
                    ? undefined
                    : String(createForm.price_per_month),
            is_active: Boolean(createForm.is_active),
        }

        try {
            await axiosInstance.post(API_LIST_BASE, payload)
            setCreating(false)
            setCreateForm((s) => ({ ...s, code: '', name: '' }))
            // Reset về trang 1 để thấy item mới
            onChange({ page: 1, page_size: pageSize })
        } catch (e) {
            alert(String(e?.response?.data?.detail || 'Failed to create plan'))
        }
    }

    const startEdit = (row) => {
        setEditing(row)
        setEditForm({
            code: row.code ?? '',
            name: row.name ?? '',
            openstack_id: row.openstack_id ?? '',
            plan_category_id: row.plan_category_id ?? '',
            compute_type_id: row.compute_type_id ?? '',
            vcpu: row.vcpu ?? '',
            ram_gb: row.ram_gb ?? '',
            storage_gb: row.storage_gb ?? '',
            storage_kind: row.storage_kind ?? '',
            bandwidth_tb: row.bandwidth_tb ?? '',
            price_per_month: row.price_per_month ?? '',
            is_active: Boolean(row.is_active),
        })
    }

    const onUpdate = async () => {
        if (!editing?.id) {
            alert('Missing ID for update.')
            return
        }
        const id = editing.id
        const payload = {
            ...editForm,
            plan_category_id:
                editForm.plan_category_id !== ''
                    ? Number(editForm.plan_category_id)
                    : undefined,
            compute_type_id:
                editForm.compute_type_id !== ''
                    ? Number(editForm.compute_type_id)
                    : undefined,
            vcpu: editForm.vcpu !== '' ? Number(editForm.vcpu) : undefined,
            ram_gb:
                editForm.ram_gb !== '' ? Number(editForm.ram_gb) : undefined,
            storage_gb:
                editForm.storage_gb !== ''
                    ? Number(editForm.storage_gb)
                    : undefined,
            bandwidth_tb:
                editForm.bandwidth_tb === ''
                    ? undefined
                    : String(editForm.bandwidth_tb),
            price_per_month:
                editForm.price_per_month === ''
                    ? undefined
                    : String(editForm.price_per_month),
            is_active: Boolean(editForm.is_active),
        }

        try {
            await axiosInstance.put(`${API_ADMIN_BASE}/${id}`, payload)
            setEditing(null)
            await refetch()
        } catch (e) {
            alert(String(e?.response?.data?.detail || 'Failed to update'))
        }
    }

    const onDelete = async () => {
        if (!confirmDel?.id) return
        const id = confirmDel.id
        try {
            await axiosInstance.delete(`${API_ADMIN_BASE}/${id}`)
            setConfirmDel(null)
            await reloadCurrentPageSafe()
        } catch (e) {
            alert(String(e?.response?.data?.detail || 'Failed to delete'))
        }
    }

    // Apply/Clear filters
    const applyFilters = () => {
        setFilters({ ...draft })
        onChange({ page: 1, page_size: pageSize })
    }

    const clearFilters = () => {
        const cleared = {
            search: '',
            plan_category_id: '',
            compute_type_id: '',
            is_active: '',
        }
        setDraft(cleared)
        setFilters(cleared)
        onChange({ page: 1, page_size: pageSize })
    }

    return (
        <div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
            <div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
                {/* Breadcrumbs */}
                <BaseBreadcrumbs />

                {/* Header + actions */}
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                        Plans
                    </h1>
                    <div className="flex flex-wrap gap-2 md:ml-auto">
                        <Input
                            value={draft.search}
                            onChange={(e) =>
                                setDraft((s) => ({
                                    ...s,
                                    search: e.target.value,
                                }))
                            }
                            placeholder="Search by code or name…"
                            className="w-64"
                            aria-label="Search"
                        />

                        <Select
                            value={draft.plan_category_id}
                            onChange={(e) =>
                                setDraft((s) => ({
                                    ...s,
                                    plan_category_id: e.target.value,
                                }))
                            }
                            disabled={loadingOptions}
                        >
                            <option value="">All categories</option>
                            {planCategories.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {optionLabel(c)}
                                </option>
                            ))}
                        </Select>

                        <Select
                            value={draft.compute_type_id}
                            onChange={(e) =>
                                setDraft((s) => ({
                                    ...s,
                                    compute_type_id: e.target.value,
                                }))
                            }
                            disabled={loadingOptions}
                        >
                            <option value="">All compute types</option>
                            {computeTypes.map((ct) => (
                                <option key={ct.id} value={ct.id}>
                                    {optionLabel(ct)}
                                </option>
                            ))}
                        </Select>

                        <Select
                            value={draft.is_active}
                            onChange={(e) =>
                                setDraft((s) => ({
                                    ...s,
                                    is_active: e.target.value,
                                }))
                            }
                        >
                            <option value="">All status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>

                        <Button onClick={applyFilters} disabled={loading}>
                            Apply
                        </Button>
                        <Button onClick={clearFilters}>Clear</Button>
                        <PrimaryButton onClick={() => setCreating(true)}>
                            New
                        </PrimaryButton>
                    </div>
                </div>

                {/* Table */}
                <PaginatedTable
                    rows={rows}
                    loading={loading}
                    error={error}
                    meta={meta}
                    onChange={onChange}
                    columns={columns}
                    actions={(row) => (
                        <div className="inline-flex items-center gap-2">
                            <Button
                                onClick={() => startEdit(row)}
                                className="h-8 px-2"
                            >
                                Edit
                            </Button>
                            <DangerButton
                                onClick={() => setConfirmDel(row)}
                                className="h-8 px-2"
                            >
                                Delete
                            </DangerButton>
                        </div>
                    )}
                    pageSizeOptions={[25, 40, 60, 100]}
                    emptyText="No plans found."
                />

                {/* Create Modal */}
                <Modal
                    open={creating}
                    title="Create Plan"
                    onClose={() => setCreating(false)}
                    footer={
                        <>
                            <Button onClick={() => setCreating(false)}>
                                Cancel
                            </Button>
                            <PrimaryButton
                                onClick={onCreate}
                                disabled={loading}
                            >
                                Create
                            </PrimaryButton>
                        </>
                    }
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Code">
                            <Input
                                value={createForm.code}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        code: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Name">
                            <Input
                                value={createForm.name}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="OpenStack ID">
                            <Input
                                value={createForm.openstack_id}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        openstack_id: e.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Plan Category">
                            <Select
                                value={createForm.plan_category_id}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        plan_category_id: e.target.value,
                                    }))
                                }
                                disabled={loadingOptions}
                            >
                                <option value="">— Select a category —</option>
                                {planCategories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {optionLabel(c)}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Compute Type">
                            <Select
                                value={createForm.compute_type_id}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        compute_type_id: e.target.value,
                                    }))
                                }
                                disabled={loadingOptions}
                            >
                                <option value="">
                                    — Select a compute type —
                                </option>
                                {computeTypes.map((ct) => (
                                    <option key={ct.id} value={ct.id}>
                                        {optionLabel(ct)}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="vCPU">
                            <Input
                                type="number"
                                value={createForm.vcpu}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        vcpu: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="RAM (GB)">
                            <Input
                                type="number"
                                value={createForm.ram_gb}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        ram_gb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Storage (GB)">
                            <Input
                                type="number"
                                value={createForm.storage_gb}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        storage_gb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Storage Kind">
                            <Input
                                value={createForm.storage_kind}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        storage_kind: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Bandwidth (TB)">
                            <Input
                                value={createForm.bandwidth_tb}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        bandwidth_tb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Price / Month">
                            <Input
                                value={createForm.price_per_month}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        price_per_month: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Active?">
                            <Select
                                value={String(createForm.is_active)}
                                onChange={(e) =>
                                    setCreateForm((s) => ({
                                        ...s,
                                        is_active: e.target.value === 'true',
                                    }))
                                }
                            >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </Select>
                        </Field>
                    </div>
                </Modal>

                {/* Edit Modal */}
                <Modal
                    open={!!editing}
                    title={`Edit: ${editing?.name ?? editing?.code ?? 'Plan'}`}
                    onClose={() => setEditing(null)}
                    footer={
                        <>
                            <Button onClick={() => setEditing(null)}>
                                Cancel
                            </Button>
                            <PrimaryButton
                                onClick={onUpdate}
                                disabled={loading}
                            >
                                Save
                            </PrimaryButton>
                        </>
                    }
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Code">
                            <Input
                                value={editForm.code}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        code: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Name">
                            <Input
                                value={editForm.name}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="OpenStack ID">
                            <Input
                                value={editForm.openstack_id}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        openstack_id: e.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Plan Category">
                            <Select
                                value={editForm.plan_category_id}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        plan_category_id: e.target.value,
                                    }))
                                }
                                disabled={loadingOptions}
                            >
                                <option value="">— Select a category —</option>
                                {planCategories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {optionLabel(c)}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Compute Type">
                            <Select
                                value={editForm.compute_type_id}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        compute_type_id: e.target.value,
                                    }))
                                }
                                disabled={loadingOptions}
                            >
                                <option value="">
                                    — Select a compute type —
                                </option>
                                {computeTypes.map((ct) => (
                                    <option key={ct.id} value={ct.id}>
                                        {optionLabel(ct)}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="vCPU">
                            <Input
                                type="number"
                                value={editForm.vcpu}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        vcpu: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="RAM (GB)">
                            <Input
                                type="number"
                                value={editForm.ram_gb}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        ram_gb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Storage (GB)">
                            <Input
                                type="number"
                                value={editForm.storage_gb}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        storage_gb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Storage Kind">
                            <Input
                                value={editForm.storage_kind}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        storage_kind: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Bandwidth (TB)">
                            <Input
                                value={editForm.bandwidth_tb}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        bandwidth_tb: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Price / Month">
                            <Input
                                value={editForm.price_per_month}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        price_per_month: e.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Active?">
                            <Select
                                value={String(editForm.is_active)}
                                onChange={(e) =>
                                    setEditForm((s) => ({
                                        ...s,
                                        is_active: e.target.value === 'true',
                                    }))
                                }
                            >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </Select>
                        </Field>
                    </div>

                    {editing ? (
                        <Field label="Other fields (read-only)">
                            <pre className="text-xs p-3 rounded-lg bg-slate-800 border border-slate-700 overflow-auto max-h-56">
                                {prettyJSON(
                                    Object.fromEntries(
                                        Object.entries(editing || {}).filter(
                                            ([k]) =>
                                                ![
                                                    'code',
                                                    'name',
                                                    'openstack_id',
                                                    'plan_category_id',
                                                    'compute_type_id',
                                                    'vcpu',
                                                    'ram_gb',
                                                    'storage_gb',
                                                    'storage_kind',
                                                    'bandwidth_tb',
                                                    'price_per_month',
                                                    'is_active',
                                                ].includes(k)
                                        )
                                    )
                                )}
                            </pre>
                        </Field>
                    ) : null}
                </Modal>

                {/* Delete confirm */}
                <Modal
                    open={!!confirmDel}
                    title="Delete plan"
                    onClose={() => setConfirmDel(null)}
                    footer={
                        <>
                            <Button onClick={() => setConfirmDel(null)}>
                                Cancel
                            </Button>
                            <DangerButton onClick={onDelete}>
                                Delete
                            </DangerButton>
                        </>
                    }
                >
                    <p className="text-slate-300">
                        Are you sure you want to delete{' '}
                        <span className="font-semibold">
                            {confirmDel?.name ??
                                confirmDel?.code ??
                                'this item'}
                        </span>
                        ?
                    </p>
                </Modal>
            </div>
        </div>
    )
}
